/**
 * A form generated from a JSON Schema (the subset tool manifests use), with a plain
 * "Field: before → after" change list and a raw JSON switch for experts (ADR-0065).
 *
 * Stable API (shared by the inbox "Edit and approve", chat approvals and — once unified — the
 * builder's tool arguments):
 *
 *   <SchemaForm schema value onChange idPrefix? labelFor? raw? onRawChange? disabled? />
 *   <SchemaDiff schema before after labelFor? />
 *   schemaFields(schema, value?)        // fields to render, in schema order (+ extra keys)
 *   schemaFormIssues(schema, value)     // [{field, code, params}] — empty when valid
 *   fieldLabel(name, schema?)           // schema `title`, else "invoice_number" → "Invoice number"
 *   formatValue(value, t)               // plain text for a value (Yes/No, numbers, lists)
 *
 * Strings live in the `pickers` namespace (`schemaForm.*`). No `eval`: validation is
 * hand-written, so the console's CSP never needs `unsafe-eval`.
 */
import { Alert, Form, Input, InputNumber, Select, Switch, Typography } from "antd";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** Structural subset of JSON Schema (compatible with `@ap/api` `JsonSchemaLite`). */
export interface FormSchema {
  type?: "object" | "string" | "number" | "integer" | "boolean" | "array";
  title?: string;
  description?: string;
  properties?: Record<string, FormSchema>;
  required?: string[];
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  items?: FormSchema;
  additionalProperties?: boolean;
}

export type FieldType = "string" | "number" | "integer" | "boolean" | "object" | "array" | "any";

export interface SchemaField {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  schema: FormSchema;
  /** Present in the value but not described by the schema. */
  extra: boolean;
}

export interface SchemaIssue {
  field: string;
  code: "required" | "type" | "enum" | "min" | "max" | "minLength" | "maxLength" | "pattern" | "notAllowed";
  params?: Record<string, string | number>;
}

/** "invoice_number" / "invoiceNumber" → "Invoice number". */
export function fieldLabel(name: string, schema?: FormSchema): string {
  if (schema?.title?.trim()) return schema.title.trim();
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}

export function schemaFields(schema: FormSchema | undefined, value: Record<string, unknown> = {}): SchemaField[] {
  const props = schema?.properties ?? {};
  const required = new Set(schema?.required ?? []);
  const known: SchemaField[] = Object.entries(props).map(([name, s]) => ({
    name,
    label: fieldLabel(name, s),
    type: (s.type ?? "any") as FieldType,
    required: required.has(name),
    schema: s,
    extra: false,
  }));
  const extra: SchemaField[] = Object.keys(value)
    .filter((k) => !(k in props))
    .map((name) => ({ name, label: fieldLabel(name), type: "any", required: false, schema: {}, extra: true }));
  return [...known, ...extra];
}

const blank = (v: unknown) => v === undefined || v === null || v === "";

function typeOk(v: unknown, t: FieldType): boolean {
  switch (t) {
    case "string":
      return typeof v === "string";
    case "number":
      return typeof v === "number" && Number.isFinite(v);
    case "integer":
      return typeof v === "number" && Number.isInteger(v);
    case "boolean":
      return typeof v === "boolean";
    case "array":
      return Array.isArray(v);
    case "object":
      return !!v && typeof v === "object" && !Array.isArray(v);
    default:
      return true;
  }
}

/** Field-level problems, with codes the form translates (`pickers:schemaForm.issue.<code>`). */
export function schemaFormIssues(schema: FormSchema | undefined, value: Record<string, unknown>): SchemaIssue[] {
  const issues: SchemaIssue[] = [];
  for (const f of schemaFields(schema, value)) {
    const v = value[f.name];
    const s = f.schema;
    if (f.extra) {
      if (schema?.additionalProperties === false) issues.push({ field: f.name, code: "notAllowed" });
      continue;
    }
    if (blank(v)) {
      if (f.required) issues.push({ field: f.name, code: "required" });
      continue;
    }
    if (!typeOk(v, f.type)) {
      issues.push({ field: f.name, code: "type", params: { type: f.type } });
      continue;
    }
    if (s.enum && !s.enum.some((x) => JSON.stringify(x) === JSON.stringify(v))) issues.push({ field: f.name, code: "enum" });
    if (typeof v === "number") {
      if (s.minimum !== undefined && v < s.minimum) issues.push({ field: f.name, code: "min", params: { n: s.minimum } });
      if (s.maximum !== undefined && v > s.maximum) issues.push({ field: f.name, code: "max", params: { n: s.maximum } });
    }
    if (typeof v === "string") {
      if (s.minLength !== undefined && v.length < s.minLength) issues.push({ field: f.name, code: "minLength", params: { n: s.minLength } });
      if (s.maxLength !== undefined && v.length > s.maxLength) issues.push({ field: f.name, code: "maxLength", params: { n: s.maxLength } });
      if (s.pattern !== undefined) {
        let ok: boolean;
        try {
          ok = new RegExp(s.pattern, "u").test(v);
        } catch {
          ok = true; // an invalid pattern in a manifest is not the reviewer's problem
        }
        if (!ok) issues.push({ field: f.name, code: "pattern" });
      }
    }
  }
  return issues;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** A field's label in the reader's language: the schema's `x-label`, its title, else words. */
export function localFieldLabel(name: string, schema: FormSchema | undefined, lang?: string): string {
  const labels = (schema as { "x-label"?: Record<string, string> } | undefined)?.["x-label"];
  const own = labels && lang ? labels[lang === "ar" ? "ar" : "en"] : undefined;
  return own?.trim() || fieldLabel(name, schema);
}

const MAX_ITEMS = 5;

/**
 * Plain text for a value: Yes/No, localised numbers, comma lists, and objects as
 * "Label: value · Label: value" with the schema's labels — never raw JSON (ops-20, ADR-0102).
 */
export function formatValue(v: unknown, t: TFn, locale?: string, schema?: FormSchema): string {
  if (v === undefined || v === null || v === "") return t("schemaForm.empty");
  if (typeof v === "boolean") return v ? t("schemaForm.yes") : t("schemaForm.no");
  if (typeof v === "number") return new Intl.NumberFormat(locale).format(v);
  if (typeof v === "string") return v;
  if (Array.isArray(v)) {
    const items = v.slice(0, MAX_ITEMS).map((x) => {
      const text = formatValue(x, t, locale, schema?.items);
      return typeof x === "object" && x !== null ? `(${text})` : text;
    });
    return v.length > MAX_ITEMS ? `${items.join(", ")} … (${new Intl.NumberFormat(locale).format(v.length)})` : items.join(", ");
  }
  if (typeof v === "object") {
    const props = schema?.properties ?? {};
    return Object.entries(v as Record<string, unknown>)
      .filter(([, x]) => x !== undefined && x !== null && x !== "")
      .map(([k, x]) => `${localFieldLabel(k, props[k], locale)}: ${formatValue(x, t, locale, props[k])}`)
      .join(" · ");
  }
  return String(v);
}

export interface SchemaFormProps {
  schema: FormSchema | undefined;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  idPrefix?: string;
  /** Override labels (e.g. translated tool argument names). */
  labelFor?: (name: string, schema: FormSchema) => ReactNode;
  /** Show the raw JSON editor instead of the form (controlled); omit to manage internally. */
  raw?: boolean;
  onRawChange?: (raw: boolean) => void;
  disabled?: boolean;
}

function JsonField({ id, value, onChange, disabled, invalidText }: { id: string; value: unknown; onChange: (v: unknown) => void; disabled?: boolean; invalidText: string }) {
  const [text, setText] = useState(() => (value === undefined ? "" : JSON.stringify(value, null, 2)));
  const [bad, setBad] = useState(false);
  return (
    <>
      <Input.TextArea
        id={id}
        className="ap-mono"
        dir="ltr"
        spellCheck={false}
        autoSize={{ minRows: 2, maxRows: 10 }}
        disabled={disabled}
        value={text}
        aria-invalid={bad}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) {
            setBad(false);
            onChange(undefined);
            return;
          }
          try {
            onChange(JSON.parse(e.target.value) as unknown);
            setBad(false);
          } catch {
            setBad(true);
          }
        }}
      />
      {bad ? <Typography.Text type="danger">{invalidText}</Typography.Text> : null}
    </>
  );
}

/** The raw JSON of the whole value, for experts; applies only valid JSON objects. */
function RawEditor({ id, value, onChange, disabled }: { id: string; value: Record<string, unknown>; onChange: (v: Record<string, unknown>) => void; disabled?: boolean }) {
  const { t } = useTranslation("pickers");
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    // Follow outside changes (e.g. form edits) unless the expert is mid-edit with invalid JSON.
    if (!bad) setText(JSON.stringify(value, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div>
      <label htmlFor={id}>{t("schemaForm.rawLabel")}</label>
      <Input.TextArea
        id={id}
        className="ap-mono"
        dir="ltr"
        spellCheck={false}
        disabled={disabled}
        autoSize={{ minRows: 6, maxRows: 18 }}
        value={text}
        aria-invalid={bad}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const v = JSON.parse(e.target.value) as unknown;
            if (!v || typeof v !== "object" || Array.isArray(v)) throw new Error("not an object");
            setBad(false);
            onChange(v as Record<string, unknown>);
          } catch {
            setBad(true);
          }
        }}
      />
      {bad ? <Typography.Text type="danger">{t("schemaForm.rawInvalid")}</Typography.Text> : null}
    </div>
  );
}

export function SchemaForm({ schema, value, onChange, idPrefix = "sf", labelFor, raw, onRawChange, disabled }: SchemaFormProps) {
  const { t } = useTranslation("pickers");
  const [ownRaw, setOwnRaw] = useState(false);
  const fields = schemaFields(schema, value);
  const described = fields.some((f) => !f.extra);
  const showRaw = (raw ?? ownRaw) || !described;
  const setRaw = (v: boolean) => (onRawChange ? onRawChange(v) : setOwnRaw(v));
  const issues = schemaFormIssues(schema, value);
  const set = (name: string, v: unknown) => {
    const next = { ...value };
    if (v === undefined || v === "") delete next[name];
    else next[name] = v;
    onChange(next);
  };
  const issueText = (i: SchemaIssue) => t(`schemaForm.issue.${i.code}`, { ...(i.params ?? {}) });

  return (
    <div data-testid="schema-form">
      {described ? (
        <Form.Item style={{ marginBlockEnd: 8 }}>
          <Switch size="small" id={`${idPrefix}-raw`} checked={showRaw} onChange={setRaw} disabled={disabled} aria-describedby={`${idPrefix}-raw-help`} />{" "}
          <label htmlFor={`${idPrefix}-raw`}>{t("schemaForm.rawToggle")}</label>
          <div id={`${idPrefix}-raw-help`} style={{ fontSize: 12 }}>
            <Typography.Text type="secondary">{t("schemaForm.rawHelp")}</Typography.Text>
          </div>
        </Form.Item>
      ) : (
        <Alert type="info" showIcon style={{ marginBlockEnd: 8 }} message={t("schemaForm.noSchema")} />
      )}
      {showRaw ? (
        <RawEditor id={`${idPrefix}-json`} value={value} onChange={onChange} disabled={disabled} />
      ) : (
        <Form layout="vertical" component="div">
          {fields.map((f) => {
            const id = `${idPrefix}-${f.name}`;
            const v = value[f.name];
            const mine = issues.filter((i) => i.field === f.name);
            const label = labelFor ? labelFor(f.name, f.schema) : f.label;
            const help = mine.length ? mine.map(issueText).join(" ") : f.extra ? t("schemaForm.extra") : f.schema.description;
            let control: ReactNode;
            if (f.schema.enum) {
              control = (
                <Select
                  id={id}
                  allowClear={!f.required}
                  disabled={disabled}
                  value={v === undefined ? undefined : JSON.stringify(v)}
                  onChange={(x: string | undefined) => set(f.name, x === undefined ? undefined : (JSON.parse(x) as unknown))}
                  options={f.schema.enum.map((x) => ({ value: JSON.stringify(x), label: formatValue(x, t) }))}
                />
              );
            } else if (f.type === "boolean") {
              control = (
                <Select
                  id={id}
                  allowClear={!f.required}
                  disabled={disabled}
                  value={typeof v === "boolean" ? String(v) : undefined}
                  onChange={(x: string | undefined) => set(f.name, x === undefined ? undefined : x === "true")}
                  options={[
                    { value: "true", label: t("schemaForm.yes") },
                    { value: "false", label: t("schemaForm.no") },
                  ]}
                />
              );
            } else if (f.type === "number" || f.type === "integer") {
              control = (
                <InputNumber
                  id={id}
                  style={{ inlineSize: "100%" }}
                  disabled={disabled}
                  min={f.schema.minimum}
                  max={f.schema.maximum}
                  precision={f.type === "integer" ? 0 : undefined}
                  value={typeof v === "number" ? v : null}
                  onChange={(x) => set(f.name, x === null ? undefined : x)}
                />
              );
            } else if (f.type === "string") {
              control = (
                <Input
                  id={id}
                  dir="auto"
                  disabled={disabled}
                  maxLength={f.schema.maxLength}
                  value={typeof v === "string" ? v : v === undefined ? "" : String(v)}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              );
            } else {
              control = <JsonField id={id} value={v} disabled={disabled} invalidText={t("schemaForm.rawInvalid")} onChange={(x) => set(f.name, x)} />;
            }
            return (
              <Form.Item
                key={f.name}
                label={label}
                htmlFor={id}
                required={f.required}
                validateStatus={mine.length ? "error" : undefined}
                help={help}
                style={{ marginBlockEnd: 12 }}
                data-field={f.name}
              >
                {control}
              </Form.Item>
            );
          })}
        </Form>
      )}
    </div>
  );
}

export interface SchemaDiffProps {
  schema?: FormSchema;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  labelFor?: (name: string, schema: FormSchema) => ReactNode;
  /** Also list unchanged fields (greyed); default: changes only. */
  showUnchanged?: boolean;
}

/** "Field label: old → new" for every changed field (added / removed included). */
export function SchemaDiff({ schema, before, after, labelFor, showUnchanged = false }: SchemaDiffProps) {
  const { t, i18n } = useTranslation("pickers");
  const names = [...new Set([...Object.keys(schema?.properties ?? {}), ...Object.keys(before), ...Object.keys(after)])].filter((k) => k in before || k in after);
  const rows = names.map((name) => {
    const s = schema?.properties?.[name] ?? {};
    const same = JSON.stringify(before[name]) === JSON.stringify(after[name]);
    return { name, s, same, label: labelFor ? labelFor(name, s) : localFieldLabel(name, s, i18n.language) };
  });
  const shown = rows.filter((r) => showUnchanged || !r.same);
  if (shown.length === 0) return <Typography.Text type="secondary">{t("schemaForm.noChanges")}</Typography.Text>;
  return (
    <ul style={{ margin: 0, paddingInlineStart: 20 }} data-testid="schema-diff">
      {shown.map((r) => (
        <li key={r.name} data-change={r.same ? "same" : !(r.name in before) ? "added" : !(r.name in after) ? "removed" : "changed"} style={r.same ? { opacity: 0.7 } : undefined}>
          <strong>{r.label}</strong>
          {": "}
          {r.same ? (
            <span dir="auto">{formatValue(after[r.name], t, i18n.language, r.s)}</span>
          ) : (
            <>
              <del dir="auto">{formatValue(before[r.name], t, i18n.language, r.s)}</del>
              <span aria-hidden> {i18n.dir() === "rtl" ? "←" : "→"} </span>
              <span className="ap-visually-hidden"> {t("schemaForm.becomes")} </span>
              <ins dir="auto" style={{ textDecoration: "none", fontWeight: 600 }}>
                {formatValue(after[r.name], t, i18n.language, r.s)}
              </ins>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
